import {AttrsSchema, Component} from 'panel';
import debounce from 'lodash/debounce';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {downloadCSV, fetchUsers, fetchUsersCount} from 'common/report/queries/query-api/users-client';
import {Cohort} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {PropertySectionItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import {MPProperties} from 'common/constants';
import {PropertyType, ReportName} from 'common/report/constants';
import {asPropertyItem} from 'common/widgets/users-table/util';
import {addToast, closeToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {CohortActionEvent, CohortDetail} from 'common/widgets/create-cohort-button';

import {TrackingEvent} from '../tracking';
import {SidebarUserListJsxProps} from './types';
import {template} from './template';
import css from './index.inline.styl';

const USER_PROPERTIES: PropertySectionItem[] = [
  asPropertyItem(MPProperties.Name, PropertyType.String),
  asPropertyItem(MPProperties.Email, PropertyType.String),
];
const GROUP_PROPERTIES: PropertySectionItem[] = [asPropertyItem(MPProperties.Name, PropertyType.String)];

const USER_COLUMNS = [
  {value: MPProperties.NameWithFallbacks, resourceType: `user`},
  {value: MPProperties.Email, resourceType: `user`},
  {value: MPProperties.LastSeen, resourceType: `user`},
  {value: MPProperties.Avatar, resourceType: `user`},
  {value: MPProperties.City, resourceType: `user`},
];
const GROUP_COLUMNS = [
  {value: MPProperties.NameWithFallbacks, resourceType: `user`},
  {value: MPProperties.LastSeen, resourceType: `user`},
  {value: MPProperties.Avatar, resourceType: `user`},
  {value: MPProperties.City, resourceType: `user`},
];

export class SidebarUserList extends Component<SidebarUserListJsxProps['state'], SidebarUserListJsxProps['attrs']> {
  fetchUsersAbortController?: AbortController;
  fetchUsersCountAbortController?: AbortController;

  static get attrsSchema(): AttrsSchema<SidebarUserListJsxProps['attrs']> {
    return {
      'hide-cohort-functionality': `boolean`,
      'include-all-users': `boolean`,
      'data-group-name': `string`,
      'segment-label': `string`,
      'report-name': {
        type: `string`,
        enum: [ReportName.Insights, ReportName.Flows, ReportName.Funnels, ReportName.Retention],
      },
      color: `string`,
      segments: {
        type: `json`,
        default: [],
      },
    };
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        totalUsers: 0,
        error: ``,
        cohort: new Cohort(),
        cohortDetails: [],
        search: ``,
        users: [],
        loading: true,
        loadingCount: true,
      },
    };
  }

  set cohort(cohort: Cohort) {
    this.update({
      cohort,
    });
  }

  set cohortDetails(cohortDetails: CohortDetail[]) {
    this.update({
      cohortDetails,
    });
  }

  connectedCallback() {
    super.connectedCallback();
    this.fetchUsers();
    this.fetchUsersCount();
  }

  getGroupColumns() {
    return GROUP_COLUMNS.map((col) => ({...col, dataGroupId: this.state.cohort.dataGroupId}));
  }

  fetchUsers = debounce(async ({search = ``}: {search?: string} = {}) => {
    this.update({
      loading: true,
      ...(search ? {loadingCount: true} : {}),
    });
    try {
      if (this.fetchUsersAbortController) {
        this.fetchUsersAbortController.abort();
      }
      this.fetchUsersAbortController = new AbortController();
      const users = await fetchUsers({
        cohort: this.state.cohort,
        includeAllUsers: this.attr(`include-all-users`),
        search,
        columns: this.state.cohort.dataGroupId ? this.getGroupColumns() : USER_COLUMNS,
        abortSignal: this.fetchUsersAbortController.signal,
      });
      this.update({
        loading: false,
        users,
        // search is not supported in the `/stat` endpoint for now, we'll have to use the total
        // users returned from /engage endpoint
        ...(search ? {totalUsers: users.length, loadingCount: false} : {}),
      });
    } catch (err) {
      if (err.name === `AbortError`) {
        return;
      }
      this.update({
        error: err.message,
        loading: false,
        ...(search ? {loadingCount: false} : {}),
      });
    }
  }, 300);

  async fetchUsersCount({search = ``}: {search?: string} = {}) {
    if (this.fetchUsersCountAbortController) {
      this.fetchUsersCountAbortController.abort();
    }
    this.fetchUsersCountAbortController = new AbortController();
    // search does not work in the fetchUsersCount endpoint for now
    if (search) {
      return;
    }
    this.update({
      loadingCount: true,
    });
    try {
      const totalUsers = await fetchUsersCount({
        cohort: this.state.cohort,
        includeAllUsers: this.attr(`include-all-users`),
        abortSignal: this.fetchUsersCountAbortController.signal,
      });
      this.update({
        loadingCount: false,
        totalUsers,
      });
    } catch (err) {
      if (err.name === `AbortError`) {
        return;
      }
      this.update({
        loadingCount: false,
        error: err.message,
      });
    }
  }

  handleUserSelected = (ev: CustomEvent) => {
    this.dispatchEvent(
      new CustomEvent(`userSelected`, {
        detail: {
          distinctID: ev.detail.distinctID,
        },
        composed: true,
      }),
    );
  };

  handleDownloadCsv = async () => {
    const toast = addToast({message: `Exporting CSV...`, timeout: null});
    try {
      const url = await downloadCSV({
        cohort: this.state.cohort,
        includeAllUsers: this.attr(`include-all-users`),
        search: this.state.search,
        columns: this.state.cohort.dataGroupId ? this.getGroupColumns() : USER_COLUMNS,
        queryHref: window.location.href,
      });
      window.location.assign(url);
      mixpanel.track(TrackingEvent.ExportCSV, {
        'Report name': this.attr(`report-name`),
        ...this.state.cohort.toTrackingData(),
      });
      closeToast(toast);
    } catch (err) {
      addToast({
        message: `Sorry, something went wrong. Please contact support if this problem persists.`,
        type: ToastType.ERROR,
        timeout: null,
      });
    }
  };

  handleSearch = (ev) => {
    this.update({search: ev.target.value});
    this.fetchUsers({search: ev.target.value});
    this.fetchUsersCount({search: ev.target.value});
  };

  handleCohortEvent = (eventName: string) => (ev: CohortActionEvent) => {
    const cohortDetail = this.state.cohortDetails.find((cohort) => cohort.type === ev.detail.type);
    const trackingData = Object.assign(cohortDetail.cohort.toTrackingData(), {
      'Cohort: has attribution filter': cohortDetail.cohort.isAttributionCohort(),
    });
    mixpanel.track(eventName, {
      'Report name': this.attr(`report-name`),
      ...trackingData,
    });
  };

  getShowingProperties() {
    return this.state.cohort.dataGroupId ? GROUP_PROPERTIES : USER_PROPERTIES;
  }
}

export const SidebarUserListEl = defineAndMakeJsxFactory(`mp-sidebar-user-list`, SidebarUserList);
export const SidebarUserListReact = panel2react(`mp-sidebar-user-list`, SidebarUserList);
